import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Bar, Radar, Scatter } from 'react-chartjs-2';
import type { ChartData } from 'chart.js';
import {
  FiTrendingUp, FiTrendingDown, FiBarChart, FiPieChart,
  FiActivity, FiTarget, FiArrowUp, FiArrowDown, FiMinus,
  FiFilter, FiCalendar, FiDownload, FiRefreshCw, FiInfo,
  FiChevronDown, FiChevronRight, FiSettings
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

export interface DataPoint {
  timestamp: Date;
  value: number;
  label?: string;
  category?: string;
}

export interface Dataset {
  id: string;
  name: string;
  data: DataPoint[];
  color?: string;
  metadata?: Record<string, any>;
}

export interface ComparisonConfig {
  type: 'trend' | 'correlation' | 'distribution' | 'performance';
  timeRange: {
    start: Date;
    end: Date;
  };
  metrics: string[];
  groupBy?: 'hour' | 'day' | 'week' | 'month';
  filters?: Record<string, any>;
  normalizeData?: boolean;
  showConfidenceInterval?: boolean;
}

export interface ComparisonResult {
  summary: {
    totalChange: number;
    averageGrowth: number;
    correlation: number;
    significance: 'high' | 'medium' | 'low';
  };
  insights: Array<{
    type: 'trend' | 'anomaly' | 'correlation' | 'forecast';
    message: string;
    confidence: number;
    data?: any;
  }>;
  statistics: {
    mean: number;
    median: number;
    stdDev: number;
    variance: number;
    skewness: number;
  };
}

interface ComparativeAnalysisProps {
  datasets: Dataset[];
  config: ComparisonConfig;
  onConfigChange?: (config: ComparisonConfig) => void;
  onExport?: (format: 'png' | 'pdf' | 'csv') => void;
  isLoading?: boolean;
  className?: string;
}

const ComparativeAnalysis: React.FC<ComparativeAnalysisProps> = ({
  datasets,
  config,
  onConfigChange,
  onExport,
  isLoading = false,
  className = ''
}) => {
  const { themeMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'correlations' | 'insights'>('overview');
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>(datasets.map(d => d.id));
  const [showFilters, setShowFilters] = useState(false);
  const [customTimeRange, setCustomTimeRange] = useState(false);

  // Color palette for datasets
  const colorPalette = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#F97316', '#06B6D4', '#84CC16', '#EC4899', '#6B7280'
  ];

  // Filter datasets based on selection and time range
  const filteredDatasets = useMemo(() => {
    return datasets
      .filter(dataset => selectedDatasets.includes(dataset.id))
      .map(dataset => ({
        ...dataset,
        data: dataset.data.filter(point => 
          point.timestamp >= config.timeRange.start && 
          point.timestamp <= config.timeRange.end
        ),
        color: dataset.color || colorPalette[datasets.indexOf(dataset) % colorPalette.length]
      }));
  }, [datasets, selectedDatasets, config.timeRange]);

  // Calculate comparison results
  const analysisResults = useMemo((): ComparisonResult => {
    if (filteredDatasets.length === 0) {
      return {
        summary: { totalChange: 0, averageGrowth: 0, correlation: 0, significance: 'low' },
        insights: [],
        statistics: { mean: 0, median: 0, stdDev: 0, variance: 0, skewness: 0 }
      };
    }

    const allValues = filteredDatasets.flatMap(d => d.data.map(p => p.value));
    const firstValues = filteredDatasets.map(d => d.data[0]?.value || 0);
    const lastValues = filteredDatasets.map(d => d.data[d.data.length - 1]?.value || 0);
    
    const totalChange = lastValues.reduce((sum, val, i) => sum + (val - firstValues[i]), 0);
    const averageGrowth = totalChange / filteredDatasets.length;
    
    // Calculate correlation (simplified)
    let correlation = 0;
    if (filteredDatasets.length >= 2) {
      const dataset1 = filteredDatasets[0].data.map(p => p.value);
      const dataset2 = filteredDatasets[1].data.map(p => p.value);
      correlation = calculateCorrelation(dataset1, dataset2);
    }

    // Calculate statistics
    const mean = allValues.reduce((sum, val) => sum + val, 0) / allValues.length;
    const variance = allValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / allValues.length;
    const stdDev = Math.sqrt(variance);
    const sortedValues = [...allValues].sort((a, b) => a - b);
    const median = sortedValues.length % 2 === 0 
      ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
      : sortedValues[Math.floor(sortedValues.length / 2)];

    // Generate insights
    const insights = generateInsights(filteredDatasets, { mean, correlation, totalChange });

    return {
      summary: {
        totalChange,
        averageGrowth,
        correlation,
        significance: Math.abs(correlation) > 0.7 ? 'high' : Math.abs(correlation) > 0.3 ? 'medium' : 'low'
      },
      insights,
      statistics: { mean, median, stdDev, variance, skewness: 0 }
    };
  }, [filteredDatasets]);

  // Calculate correlation coefficient
  const calculateCorrelation = (x: number[], y: number[]): number => {
    const n = Math.min(x.length, y.length);
    if (n === 0) return 0;

    const sumX = x.slice(0, n).reduce((a, b) => a + b, 0);
    const sumY = y.slice(0, n).reduce((a, b) => a + b, 0);
    const sumXY = x.slice(0, n).reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumXX = x.slice(0, n).reduce((sum, xi) => sum + xi * xi, 0);
    const sumYY = y.slice(0, n).reduce((sum, yi) => sum + yi * yi, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  };

  // Generate insights based on analysis
  const generateInsights = (datasets: Dataset[], stats: any) => {
    const insights: ComparisonResult['insights'] = [];

    // Trend analysis
    datasets.forEach(dataset => {
      if (dataset.data.length < 2) return;
      
      const first = dataset.data[0].value;
      const last = dataset.data[dataset.data.length - 1].value;
      const change = ((last - first) / first) * 100;

      if (Math.abs(change) > 10) {
        insights.push({
          type: 'trend' as const,
          message: `${dataset.name} shows a ${change > 0 ? 'positive' : 'negative'} trend of ${Math.abs(change).toFixed(1)}%`,
          confidence: Math.min(Math.abs(change) / 50, 1),
        });
      }
    });

    // Correlation insights
    if (stats.correlation > 0.7) {
      insights.push({
        type: 'correlation' as const,
        message: `Strong positive correlation detected between datasets (${(stats.correlation * 100).toFixed(1)}%)`,
        confidence: Math.abs(stats.correlation),
      });
    } else if (stats.correlation < -0.7) {
      insights.push({
        type: 'correlation' as const,
        message: `Strong negative correlation detected between datasets (${Math.abs(stats.correlation * 100).toFixed(1)}%)`,
        confidence: Math.abs(stats.correlation),
      });
    }

    return insights;
  };

  // Prepare chart data
  const chartData: ChartData<'line', number[], string> = useMemo(() => {
    // Always return consistent structure with string labels for line-based charts
    const labels = filteredDatasets[0]?.data.map(point =>
      format(point.timestamp, config.groupBy === 'hour' ? 'HH:mm' : 'MMM dd')
    ) || [];

    // When correlation is selected, we won't use this chartData (we render a Scatter chart),
    // but we still return a valid line dataset to satisfy typing.
    return {
      labels,
      datasets: filteredDatasets.map(dataset => ({
        label: dataset.name,
        data: dataset.data.map(point => point.value),
        borderColor: dataset.color || '#3B82F6',
        backgroundColor: (dataset.color || '#3B82F6') + (config.type === 'trend' ? '20' : '80'),
        fill: config.type === 'trend',
        tension: 0.4,
      }))
    };
  }, [filteredDatasets, config.type, config.groupBy]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: config.type !== 'correlation' ? {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Value'
        }
      }
    } : undefined,
  };

  // Update time range
  const updateTimeRange = (days: number) => {
    const end = new Date();
    const start = subDays(end, days);
    onConfigChange?.({
      ...config,
      timeRange: { start: startOfDay(start), end: endOfDay(end) }
    });
  };

  // Toggle dataset selection
  const toggleDataset = (datasetId: string) => {
    setSelectedDatasets(prev => 
      prev.includes(datasetId) 
        ? prev.filter(id => id !== datasetId)
        : [...prev, datasetId]
    );
  };

  // Get trend indicator
  const getTrendIndicator = (change: number) => {
    if (change > 5) return { icon: FiTrendingUp, color: 'text-green-500', bg: 'bg-green-50' };
    if (change < -5) return { icon: FiTrendingDown, color: 'text-red-500', bg: 'bg-red-50' };
    return { icon: FiMinus, color: 'text-gray-500', bg: 'bg-gray-50' };
  };

  return (
    <div
      className={`rounded-lg ${className}`}
      style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
        <div className="flex items-center gap-3">
          <FiBarChart size={20} className="text-blue-500" />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Comparative Analysis</h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${showFilters ? 'bg-blue-500/10 text-blue-400' : 'hover:bg-gray-100/10'}`}
            style={{ color: 'var(--color-text-primary)' }}
          >
            <FiFilter size={16} />
          </button>
          
          <button
            onClick={() => onExport?.('png')}
            className="p-2 rounded-lg hover:bg-gray-100/10"
            style={{ color: 'var(--color-text-primary)' }}
            title="Export"
          >
            <FiDownload size={16} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-4"
            style={{ borderBottom: '1px solid var(--color-border-primary)', background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Time Range */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Time Range
                </label>
                <div className="flex gap-2">
                  {[7, 30, 90].map(days => (
                    <button
                      key={days}
                      onClick={() => updateTimeRange(days)}
                      className="px-3 py-1 text-xs rounded hover:bg-gray-100/10"
                      style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>

              {/* Analysis Type */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Analysis Type
                </label>
                <select
                  value={config.type}
                  onChange={(e) => onConfigChange?.({
                    ...config,
                    type: e.target.value as any
                  })}
                  className="w-full px-3 py-1 rounded text-sm"
                  style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                >
                  <option value="trend">Trend Analysis</option>
                  <option value="correlation">Correlation</option>
                  <option value="distribution">Distribution</option>
                  <option value="performance">Performance</option>
                </select>
              </div>

              {/* Group By */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Group By
                </label>
                <select
                  value={config.groupBy || 'day'}
                  onChange={(e) => onConfigChange?.({
                    ...config,
                    groupBy: e.target.value as any
                  })}
                  className="w-full px-3 py-1 rounded text-sm"
                  style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                >
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>

            {/* Dataset Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Datasets
              </label>
              <div className="flex flex-wrap gap-2">
                {datasets.map((dataset, index) => (
                  <button
                    key={dataset.id}
                    onClick={() => toggleDataset(dataset.id)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm border ${
                      selectedDatasets.includes(dataset.id)
                        ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                        : 'hover:bg-gray-100/10'
                    }`}
                    style={{
                      background: selectedDatasets.includes(dataset.id) ? undefined : 'var(--color-bg-primary)',
                      borderColor: selectedDatasets.includes(dataset.id) ? undefined : 'var(--color-border-primary)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: dataset.color || colorPalette[index] }}
                    />
                    {dataset.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Tabs */}
      <div style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
        <nav className="flex space-x-8 px-4">
          {[
            { id: 'overview', label: 'Overview', icon: FiActivity },
            { id: 'trends', label: 'Trends', icon: FiTrendingUp },
            { id: 'correlations', label: 'Correlations', icon: FiTarget },
            { id: 'insights', label: 'Insights', icon: FiInfo },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-3 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent hover:text-gray-300'
                }`}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {([
                {
                  label: 'Total Change',
                  value: analysisResults.summary.totalChange as number | string,
                  format: (val: any) => (typeof val === 'number' ? (val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)) : String(val)),
                  trend: analysisResults.summary.totalChange,
                },
                {
                  label: 'Average Growth',
                  value: analysisResults.summary.averageGrowth as number | string,
                  format: (val: any) => `${(Number(val) || 0).toFixed(1)}%`,
                  trend: analysisResults.summary.averageGrowth,
                },
                {
                  label: 'Correlation',
                  value: analysisResults.summary.correlation as number | string,
                  format: (val: any) => `${((Number(val) || 0) * 100).toFixed(1)}%`,
                  trend: analysisResults.summary.correlation,
                },
                {
                  label: 'Significance',
                  value: analysisResults.summary.significance as unknown as string,
                  format: (val: any) => String(val).charAt(0).toUpperCase() + String(val).slice(1),
                  trend: 0,
                }
              ] as Array<{label: string; value: number | string; format: (v: any) => string; trend: number}>).map((stat, index) => {
                const trend = getTrendIndicator(typeof stat.trend === 'number' ? stat.trend : 0);
                const TrendIcon = trend.icon;

                return (
                  <div
                    key={index}
                    className="p-4 rounded-lg"
                    style={{
                      background: trend.bg.includes('green')
                        ? 'rgba(16,185,129,0.08)'
                        : trend.bg.includes('red')
                        ? 'rgba(239,68,68,0.08)'
                        : 'rgba(148,163,184,0.08)',
                      border: '1px solid var(--color-border-primary)'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                          {stat.label}
                        </p>
                        <p className="text-xl font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }}>
                          {stat.format(stat.value as any)}
                        </p>
                      </div>
                      <TrendIcon className={`${trend.color} opacity-75`} size={20} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Chart */}
            <div className="h-96 rounded-lg p-4" style={{ background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <FiRefreshCw className="animate-spin mr-2" />
                  Loading analysis...
                </div>
              ) : (
                config.type === 'correlation' && filteredDatasets.length >= 2 ? (
                  <Scatter data={{
                    datasets: [{
                      label: `${filteredDatasets[0].name} vs ${filteredDatasets[1].name}`,
                      data: filteredDatasets[0].data.map((point, i) => ({
                        x: point.value,
                        y: filteredDatasets[1].data[i]?.value || 0
                      })),
                      backgroundColor: filteredDatasets[0].color + '80',
                      borderColor: filteredDatasets[0].color,
                    }]
                  }} options={chartOptions} />
                ) : (
                  <Line data={chartData} options={chartOptions} />
                )
              )}
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-4">
            {analysisResults.insights.length > 0 ? (
              analysisResults.insights.map((insight, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg"
                  style={{ border: '1px solid var(--color-border-primary)', background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      insight.type === 'trend' ? 'bg-blue-100 text-blue-600' :
                      insight.type === 'correlation' ? 'bg-green-100 text-green-600' :
                      insight.type === 'anomaly' ? 'bg-red-100 text-red-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {insight.type === 'trend' && <FiTrendingUp size={16} />}
                      {insight.type === 'correlation' && <FiTarget size={16} />}
                      {insight.type === 'anomaly' && <FiActivity size={16} />}
                      {insight.type === 'forecast' && <FiBarChart size={16} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{insight.message}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          <span>Confidence:</span>
                          <div className="w-16 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-border-primary)' }}>
                            <div className="h-full rounded-full" style={{ background: '#3B82F6', width: `${insight.confidence * 100}%` }} />
                          </div>
                          <span>{(insight.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
                <FiInfo size={48} className="mx-auto mb-2 opacity-50" />
                <p>No significant insights found for the current analysis.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparativeAnalysis;